import { isEqual } from 'lodash-es';
import { parse, type CookieParseOptions, type CookieSerializeOptions, serialize } from 'cookie-es';
import destr from 'destr';
import {
  setCookie as setServerCookie,
  getCookie as getServerCookie,
  deleteCookie as deleteServerCookie,
  getRequestHeader,
  getResponseHeader,
} from 'h3';

import type { NuxtApp } from '#app';

const defaultParseOption: CookieParseOptions = {
  decode: (val: string) => destr(decodeURIComponent(val)),
};

function getCookies(nuxtApp?: NuxtApp) {
  if (import.meta.server) {
    const event = useRequestEvent(nuxtApp)!;
    const requestCookies = parse(
      getRequestHeader(event, 'cookie') || '',
      defaultParseOption
    ) as unknown as Record<string, any>;
    let setCookies = getResponseHeader(event, 'set-cookie');

    if (setCookies) {
      if (!Array.isArray(setCookies)) {
        setCookies = [setCookies as string];
      }

      setCookies = setCookies.map(item => item.split(';')[0]);

      const decode = defaultParseOption.decode || (v => destr(decodeURIComponent(v)));

      const responseCookies = setCookies.reduce(
        (Obj, curr) => {
          const [name, value] = curr.split('=');
          Obj[name] = decode(value);
          return Obj;
        },
        {} as Record<string, string>
      );

      return { ...requestCookies, ...responseCookies };
    }

    return requestCookies;
  }

  return parse(document.cookie, defaultParseOption) as unknown as Record<string, string>;
}

function getCookie(name: string, nuxtApp?: NuxtApp) {
  return getCookies(nuxtApp)[name];
}

function writeServerCookie(
  event: ReturnType<typeof useRequestEvent>,
  name: string,
  value: any,
  opts = {} as CookieSerializeOptions
) {
  if (event) {
    if (value !== null && value !== undefined) {
      return setServerCookie(event, name, value, opts);
    }
    if (getServerCookie(event, name) !== undefined) {
      return deleteServerCookie(event, name, opts);
    }
  }
}

function serializeCookie(name: string, value: string, opts = {} as CookieSerializeOptions) {
  if (value === null || value === undefined) {
    return serialize(name, value, { ...opts, maxAge: -1 });
  }
  return serialize(name, value, opts);
}

const defaultSerializeOption: CookieSerializeOptions = {
  path: '/',
  maxAge: 365 * 24 * 60 * 60,
  encode: (val: string) => encodeURIComponent(typeof val === 'string' ? val : JSON.stringify(val)),
};

function setCookie(name: string, val: any, option?: CookieSerializeOptions, _nuxtApp?: NuxtApp) {
  const mergedOption = { ...defaultSerializeOption, ...option };

  if (import.meta.server) {
    const nuxtApp = _nuxtApp || tryUseNuxtApp() || undefined;
    const writeCookie = () => {
      if (!isEqual(getCookie(name, nuxtApp), val)) {
        writeServerCookie(useRequestEvent(nuxtApp), name, val, mergedOption);
      }
    };
    writeCookie();
  } else {
    document.cookie = serializeCookie(name, val, mergedOption);
  }
}

function deleteCookie(name: string, _nuxtApp?: NuxtApp) {
  setCookie(name, undefined, undefined, _nuxtApp);
}

export { setCookie, getCookie, getCookies, deleteCookie };
